import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { parseSendGuardError } from "@/lib/send-guard";

const STORAGE_KEY = "pendingEmailDrafts.v1";

export type PendingDraftReason = "gmail_disconnected" | "send_failed" | "unfilled_placeholder";

export interface PendingEmailDraft {
  id: string;
  endpoint: string;
  method: "POST" | "PATCH" | "PUT";
  payload: Record<string, any>;
  recipientEmail: string;
  subject: string;
  description?: string;
  reason: PendingDraftReason;
  createdAt: number;
}

export interface AddDraftInput {
  endpoint: string;
  method?: "POST" | "PATCH" | "PUT";
  payload: Record<string, any>;
  recipientEmail: string;
  subject: string;
  description?: string;
  reason: PendingDraftReason;
}

interface PendingDraftsContextValue {
  drafts: PendingEmailDraft[];
  addDraft: (input: AddDraftInput) => string;
  removeDraft: (id: string) => void;
  clearDrafts: () => void;
}

const Ctx = createContext<PendingDraftsContextValue | null>(null);

function loadFromStorage(): PendingEmailDraft[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((d) => d && typeof d.id === "string" && typeof d.endpoint === "string");
  } catch {
    return [];
  }
}

function saveToStorage(drafts: PendingEmailDraft[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    /* ignore quota errors */
  }
}

function genId(): string {
  if (typeof crypto !== "undefined" && typeof (crypto as any).randomUUID === "function") {
    return (crypto as any).randomUUID();
  }
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function PendingEmailDraftsProvider({ children }: { children: React.ReactNode }) {
  const [drafts, setDrafts] = useState<PendingEmailDraft[]>(() => loadFromStorage());

  // Persist on every change.
  useEffect(() => {
    saveToStorage(drafts);
  }, [drafts]);

  // Keep multiple tabs in sync — if another tab adds/sends a draft, mirror it here.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setDrafts(loadFromStorage());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addDraft = useCallback((input: AddDraftInput) => {
    const id = genId();
    const draft: PendingEmailDraft = {
      id,
      endpoint: input.endpoint,
      method: input.method ?? "POST",
      payload: input.payload,
      recipientEmail: input.recipientEmail,
      subject: input.subject,
      description: input.description,
      reason: input.reason,
      createdAt: Date.now(),
    };
    setDrafts((prev) => [...prev, draft]);
    return id;
  }, []);

  const removeDraft = useCallback((id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const clearDrafts = useCallback(() => {
    setDrafts([]);
  }, []);

  const value = useMemo<PendingDraftsContextValue>(
    () => ({ drafts, addDraft, removeDraft, clearDrafts }),
    [drafts, addDraft, removeDraft, clearDrafts],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePendingEmailDrafts(): PendingDraftsContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("usePendingEmailDrafts must be used within PendingEmailDraftsProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// useSendEmailWithPreflight
// ---------------------------------------------------------------------------
// Wraps the existing /api/email/* send endpoints with a quick connection
// pre-flight check. If Gmail is disconnected (and no workspace fallback is
// available), the message is held as a draft in localStorage and the global
// reconnect prompt surfaces a "Connect Gmail" call to action. Once Gmail is
// reconnected the user can re-send the held draft from the prompt.
// ---------------------------------------------------------------------------
export interface SendEmailArgs {
  endpoint: string;
  method?: "POST" | "PATCH" | "PUT";
  payload: Record<string, any>;
  recipientEmail: string;
  subject: string;
  /** Short label shown in toasts/prompt e.g. "booking confirmation". */
  description?: string;
}

export interface SendEmailResult {
  ok: boolean;
  queued: boolean;
  data?: any;
  error?: any;
  draftId?: string;
}

export interface SendEmailCallbacks {
  onSent?: (data: any) => void;
  onQueued?: (draftId: string, reason: PendingDraftReason) => void;
  onError?: (error: any) => void;
}

interface PreflightResponse {
  canSend: boolean;
  userTokensConnected: boolean;
  fallbackAvailable: boolean;
  reason?: string;
}

async function fetchPreflight(): Promise<PreflightResponse | null> {
  try {
    const res = await fetch("/api/email/preflight", { credentials: "include" });
    if (!res.ok) return null;
    return (await res.json()) as PreflightResponse;
  } catch {
    return null;
  }
}

/** True when the error message text looks like an HTML page (e.g. Google GFE 403). */
function looksLikeHtml(text: string): boolean {
  return (
    /^\s*<(?:!doctype|html|head|meta|title|body)/i.test(text) ||
    /<title>\s*\d{3}\s*<\/title>/i.test(text)
  );
}

function isReconnectError(error: any): boolean {
  const message = String(error?.message ?? "");
  // apiRequest throws Error("<status>: <text>") — match 503 as reconnect signal.
  if (message.startsWith("503:")) return true;
  // Google returns a raw HTML 403 page (GFE) when an OAuth token is revoked or
  // lacks required scope. The body starts with <!doctype html>. Treat any
  // 401/403 + HTML-looking body as a reconnect signal so the draft is held
  // in localStorage rather than showing a raw HTML error toast.
  if (/^(401|403):/.test(message)) {
    const bodyPart = message.slice(message.indexOf(":") + 1).trim();
    if (looksLikeHtml(bodyPart)) return true;
    // queryClient's throwIfResNotOk now sanitizes Google's raw HTML 403 page
    // into a clean sentence BEFORE this runs, so also match that sanitized
    // wording (and any auth/reconnect phrasing) as a reconnect signal.
    if (/mailbox connection|authorization was rejected|reconnect/i.test(bodyPart)) return true;
  }
  // Best-effort body parse for a needsReconnect flag.
  const m = message.match(/^\d{3}:\s*([\s\S]+)$/);
  if (m) {
    try {
      const body = JSON.parse(m[1]);
      if (body && body.needsReconnect === true) return true;
      // The 503 JSON body may embed raw Google HTML in the message field
      // (when both the primary and workspace-fallback Gmail accounts fail).
      // Treat that as a reconnect error too.
      if (body && typeof body.message === "string" && looksLikeHtml(body.message)) return true;
    } catch {
      /* not JSON */
    }
  }
  return false;
}

export function useSendEmailWithPreflight() {
  const { addDraft } = usePendingEmailDrafts();
  const { toast } = useToast();

  return useCallback(
    async (args: SendEmailArgs, callbacks?: SendEmailCallbacks): Promise<SendEmailResult> => {
      // Step 1: lightweight pre-flight. If the network call itself fails we
      // assume best-effort and try to send anyway — better to attempt the real
      // send than to falsely hold a message as a draft.
      const preflight = await fetchPreflight();
      const canSend = preflight === null ? true : preflight.canSend;

      if (!canSend) {
        const draftId = addDraft({
          endpoint: args.endpoint,
          method: args.method ?? "POST",
          payload: args.payload,
          recipientEmail: args.recipientEmail,
          subject: args.subject,
          description: args.description,
          reason: "gmail_disconnected",
        });
        toast({
          title: "Gmail disconnected — message saved",
          description: `Reconnect Gmail to send your ${args.description || "message"}.`,
        });
        callbacks?.onQueued?.(draftId, "gmail_disconnected");
        return { ok: false, queued: true, draftId };
      }

      // Step 2: actually send.
      try {
        const response = await apiRequest(args.method ?? "POST", args.endpoint, args.payload);
        const data = await response.json().catch(() => ({}));
        callbacks?.onSent?.(data);
        return { ok: true, queued: false, data };
      } catch (err: any) {
        // Unfilled merge variable — server already saved the draft;
        // just surface a friendly toast and call onQueued so callers
        // can close their compose panel.
        const guardDetails = parseSendGuardError(err);
        if (guardDetails.isGuardError && guardDetails.code === "UNRENDERED_PLACEHOLDER") {
          const tokenList = guardDetails.tokens?.join(", ") || "unknown token";
          const draftNote = guardDetails.draftUrl
            ? " Your message was saved to Drafts — open it there to fix and resend."
            : " We saved your message — fix the template and re-send it from Drafts.";
          toast({
            title: "Message saved to drafts",
            description: `Your template has ${tokenList} which couldn't be filled in automatically.${draftNote}`,
          });
          // Use the server-assigned draftId when available; fall back to a
          // local placeholder so callers that key off draftId still work.
          const draftId = guardDetails.draftId ?? `placeholder-${Date.now()}`;
          callbacks?.onQueued?.(draftId, "unfilled_placeholder");
          return { ok: false, queued: true, draftId };
        }

        if (isReconnectError(err)) {
          const draftId = addDraft({
            endpoint: args.endpoint,
            method: args.method ?? "POST",
            payload: args.payload,
            recipientEmail: args.recipientEmail,
            subject: args.subject,
            description: args.description,
            reason: "send_failed",
          });
          toast({
            title: "Could not send — message saved",
            description: `We saved your ${args.description || "message"}. Reconnect Gmail to send it.`,
          });
          callbacks?.onQueued?.(draftId, "send_failed");
          return { ok: false, queued: true, draftId };
        }
        callbacks?.onError?.(err);
        return { ok: false, queued: false, error: err };
      }
    },
    [addDraft, toast],
  );
}
